import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import { apiClient } from 'api/trpc/trpc-client';
import Loader from 'reusable/mobx-helpers/Loader';
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto';
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore';
import { notNull } from '@gap/utils/utils';
import { GapError } from '@trpc-endpoints/trpc/error-handler/GapError';
import RouterStore from 'features/main/main-router/org-guard/RouterStore';
import DateUtils from '@gap/utils/date-utils';
import ProposalsViewSign from './view/ProposalsViewSign';
import ProposalsEdit from './edit/ProposalsEdit';
import { OrgDto } from '@gap/backend/app-services/org/models/OrgDto';
import { IDBProposalEntityCacheStore } from 'cache/idb/IDBProposalEntityCacheStore';
import ModalState from 'reusable/mobx-helpers/ModalState';
import { ShareDocumentDialogProps } from 'reusable/share-document-dialog/ShareDocumentDialog';
import { AdvisorClientContactDto } from '@gap/backend/app-services/advisor/client/contact/models/AdvisorClientContactDto';
import { DocShareDto } from '@gap/backend/app-services/doc-share/models/DocShareDto';

interface ProposalsViewStoreProps {
   proposalId: string,
   orgGuardStore: OrgGuardStore,
   routerStore: RouterStore,
}

export default class ProposalsViewStore {
   sp
   loader = new Loader(true)
   proposal?: ProposalDto
   advisorOrg: OrgDto.minimal
   clientOrg?: OrgDto.minimal
   showSignedConfirmation: boolean = false
   shareDialogModalState = new ModalState()
   shareDialogLoader = new Loader(true)
   clientContacts: AdvisorClientContactDto.minimal[] = []
   clientXeroId?: string
   documentAccessRights: DocShareDto.DocumentAccessRights[] = []
   userHasAccessToProposal = true

   constructor(sp: ProposalsViewStoreProps) {
      this.sp = sp
      const currentOrg = notNull(sp.orgGuardStore.org)
      if (currentOrg.orgType === 'advisor') {
         this.advisorOrg = currentOrg
      } else if (currentOrg.orgType === 'client') {
         this.clientOrg = currentOrg
         this.advisorOrg = notNull(currentOrg.advisorOrg)
      }
   }

   get location() {
      return this.sp.orgGuardStore.sp.regionRouterStore.sp.location
   }

   get shouldShowPreviewAnnouncement() {
      return this.sp.orgGuardStore.userOrg.orgType === 'advisor' && this.location.hash === '#preview'
   }

   get mode() {
      if (!this.proposal) {
         return undefined
      }

      if (this.proposal.sentAt || this.shouldShowPreviewAnnouncement) {
         if (!this.showSignedConfirmation && this.proposal.acceptedAt) {
            return 'view-accepted'
         }
         return 'view-sign'
      }

      return 'edit'
   }

   get proposalTitle() {
      return [
         'Proposal',
         ...(this.clientOrg?.name ? [`for ${this.clientOrg?.name}`] : []),
         ...(this.proposal?.sentAt ? [DateUtils.formatMonthAndYear(new Date(this.proposal.sentAt))] : []),
      ].join(' ')
   }

   get ProposalContent() {
      switch (this.mode) {
         case undefined:
         case 'view-accepted':
            return undefined
         case 'view-sign':
            return ProposalsViewSign
         case 'edit':
            return ProposalsEdit
      }
   }

   get shareDocument(): DocShareDto.Document {
      return {
         type: 'proposal-document',
         proposalId: this.sp.proposalId,
         name: this.proposalTitle,
      }
   }

   get shareDocumentDialogProps(): ShareDocumentDialogProps | null {
      if (!this.proposal || !this.clientOrg) {
         return null
      }
      return {
         client: {
            advisorClientId: this.proposal.advisorClientId!,
            contacts: this.clientContacts,
            orgId: this.clientOrg.id,
            xeroId: this.clientXeroId,
            name: this.clientOrg.name
         },
         documentAccessRights: this.documentAccessRights,
         modalState: this.shareDialogModalState,
         document: this.shareDocument
      }
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         try {
            this.proposal = notNull(await IDBProposalEntityCacheStore.getById(this.sp.proposalId, this.sp.orgGuardStore.org!), `Proposal ${this.sp.proposalId} not found`)

            if (!this.clientOrg && this.proposal?.advisorClientId) {
               this.clientOrg = await apiClient.advisor.getClientOrgByAdvisorClientId.query({ advisorClientId: this.proposal.advisorClientId })
               if (this.proposal.acceptedAt) {
                  // Don't await. It has a loader that takes care of it and I dont want it to block the proposal fetch.
                  this.fetchAdditionalInfoForShareDialog()
               }
            }

            if (this.proposal.acceptedAt) {
               this.navigateToSignedDocument()
            }

            if (this.proposal?.advisorOrgId !== this.advisorOrg.id) {
               throw new GapError.forbidden()
            }
         } catch (error: any) {
            if ([error.message, error?.data?.gapError?.message].includes(GapError.messages.forbidden.noDocumentAccessUser)) {
               this.userHasAccessToProposal = false
            }
         }
      })
   }

   fetchAdditionalInfoForShareDialog = async () => {
      await this.shareDialogLoader.executeLoading(async () => {
         const [clientDetails, documentAccessRights] = await Promise.all([
            apiClient.advisor.getMyBasicContactsForClient.query({ clientOrgId: this.clientOrg!.id }),
            apiClient.docShare.getDocumentAccessRights.query({
               clientOrgId: this.clientOrg!.id,
               documents: [this.shareDocument]
            })
         ])
         this.clientContacts = clientDetails.contacts
         this.clientXeroId = clientDetails.xeroId
         this.documentAccessRights = documentAccessRights
      })
   }

   getDocumentsRoute = () => {
      const { orgGuardStore } = this.sp

      return [
         orgGuardStore.getOrgPrefix(),
         ...(orgGuardStore.userOrg.orgType === 'advisor' ? [`clients/${notNull(this.clientOrg).id}`] : []),
         'documents'
      ].join('/');
   }

   navigateToSignedDocument = () => {
      const { regionRouterStore } = this.sp.orgGuardStore.sp
      regionRouterStore.navigate([
         this.getDocumentsRoute(),
         `proposal/${notNull(this.proposal).id}`,
      ].join('/'))
   }

   closeProposalView = () => {
      this.sp.routerStore.goToNavPath('proposals')
   }
}

export const { Provider: ProposalsViewStoreProvider, useProvidedStore: useProposalsViewStore } =
   createStoreContext(ProposalsViewStore)
